import React, { useEffect, useState } from "react";
import {
    Box,
    Grid,
    Typography,
    Chip,
    useTheme,
    Paper,
    Avatar,
    Select,
    MenuItem,
} from "@mui/material";
import { Today, AutoGraph, Groups, } from "@mui/icons-material";
import { LineChart } from "@mui/x-charts/LineChart";
import { fetchCompetitionAnalytics } from "../../services/Competition";


const getIcon = (type) => {
    switch (type) {
        case "people":
            return <Groups color="primary" />;
        case "analytics":
            return <AutoGraph color="primary" />;
        case "calendar":
            return <Today color="primary" />;
        default:
            return null;
    }
};

//filters
const filterOptions = [
    { label: "Today", value: 1 },
    { label: "Last 3 Days", value: 3 },
    { label: "Last 7 Days", value: 7 },
    { label: "Last 30 Days", value: 30 },
];




const Analytics = ({ competitionId }) => {
    const theme = useTheme();
    const [summary, setSummary] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [filter, setFilter] = useState(7);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await fetchCompetitionAnalytics(competitionId, filter);
                console.log("Fetched analytics data:", res);

                if (res?.data) {
                    const { enrollments, engagements, days_active, activity_overview } = res.data;

                    // ---- Summary ----
                    setSummary({
                        total_enrollments: {
                            label: "Total Enrollments",
                            value: enrollments.total_enrollments || 0,
                            change: `+${enrollments.today_enrollments || 0} today`,
                            icon: "people",
                        },
                        total_engagements: {
                            label: "Total Engagements",
                            value: engagements.total_engagements || 0,
                            change: `+${engagements.today_engagements || 0} today`,
                            icon: "analytics",
                        },
                        day_active: {
                            label: "Day Active",
                            value: days_active.active_since || 0,
                            change: `${days_active.days_remaining || 0} remaining`,
                            icon: "calendar",
                        },
                    });

                    // ---- Chart Mapping ----
                    if (Array.isArray(activity_overview)) {
                        const x_axis = activity_overview.map((item) =>
                            new Date(item.date).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                            })
                        );

                        const enrollmentsData = activity_overview.map(
                            (item) => item.enrollments
                        );
                        const engagementsData = activity_overview.map(
                            (item) => item.engagements
                        );

                        setChartData({
                            title: "Daily Activity Overview",
                            x_axis,
                            series: [
                                {
                                    name: "Enrollments",
                                    color: "#1976d2",
                                    data: enrollmentsData,
                                },
                                {
                                    name: "Engagements",
                                    color: "#e90000",
                                    data: engagementsData,
                                },
                            ],
                        });
                    }
                }
            } catch (error) {
                console.error("Error fetching analytics data:", error);
            }
        };

        if (competitionId) fetchData();
    }, [competitionId, filter]);


    if (!summary || !chartData) {
        return (
            <Typography variant="body2" sx={{ textAlign: "center", my: 3 }}>
                Loading analytics...
            </Typography>
        );
    }

    return (
        <React.Fragment>
            {/* ---- Summary Cards ---- */}
            <Grid container spacing={2}>
                {Object.entries(summary).map(([key, item]) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={key}>
                        <Paper elevation={0} sx={{ position: "relative" }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1.5,
                                    alignItems: "flex-start",
                                    p: 2,
                                }}
                            >
                                <Avatar sx={{ bgcolor: theme.palette.background.default }}>
                                    {getIcon(item.icon)}
                                </Avatar>
                                <Typography variant="body2" fontWeight={500}>
                                    {item.label}
                                </Typography>
                                <Typography variant="h5" fontWeight={700}>
                                    {item.value.toLocaleString()}
                                </Typography>
                                <Chip
                                    clickable={false}
                                    onClick={(e) => e.stopPropagation()}
                                    size="small"
                                    label={item.change}
                                    color="success"
                                    variant="outlined"
                                    sx={{ position: "absolute", top: 16, right: 16 }}
                                />
                            </Box>
                        </Paper>
                    </Grid>
                ))}

                {/* ---- Chart Section ---- */}
                <Grid size={12}>
                    <Paper elevation={0} sx={{ p: 2 }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                mb: 1,
                                gap: 1,
                            }}
                        >
                            <Typography variant="h6">{chartData.title}</Typography>

                            <Select
                                size="small"
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                            >
                                {filterOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>

                        </Box>

                        <Box sx={{ width: "100%", height: 320 }}>
                            <LineChart
                                series={chartData.series.map((s) => ({
                                    data: s.data,
                                    label: s.name,
                                    color: s.color,
                                }))}
                                xAxis={[{ scaleType: "point", data: chartData.x_axis }]}
                                yAxis={[{ width: 50 }]}
                                margin={{ right: 24, left: 24, top: 20, bottom: 20 }}
                                grid={{ vertical: true, horizontal: true }}
                            />
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </React.Fragment>
    );
};

export default Analytics;
